import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { Button, FormGroupLabel, TextField } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { InformationPopUp, RadioGroup, FormSpacer } from 'components';
import { FormDataType, IndOrBus, useFormState } from 'contexts/FormProvider';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';

interface ContactReasonProps {
  form: UseFormReturn<FormDataType>;
  showNextButton: boolean;
  enableNextButton: boolean;
  onSubmit: () => void;
}

type TextFieldValue = string | undefined;

const ContactReason = ({
  form,
  showNextButton = true,
  enableNextButton = false,
  onSubmit = noop,
}: ContactReasonProps) => {
  const {
    control,
    formState: { errors },
    register,
    watch,
    trigger: triggerValidation,
    setValue,
  } = form;

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const { formData } = useFormState();

  const isSmallBusiness = watch('isSmallBusiness');
  const policyOrClaim = watch('policyOrClaim') || '';
  const indOrBus = watch('indOrBus') || '';
  const isComplaint = watch('lodgement') === 'Complaint';

  React.useEffect(() => {
    register('claimNumber');
    register('policyNumber');
  }, [register, policyOrClaim]);

  const options = [
    {
      text: getTextContent('yourDetails.personalDetails.options.0.text'),
      value: 'claim',
    },
    {
      text: getTextContent('yourDetails.personalDetails.options.1.text'),
      value: 'policy',
    },
    ...(!isComplaint
      ? [
          {
            text: getTextContent('yourDetails.personalDetails.options.2.text'),
            value: 'other',
          },
        ]
      : []),
  ];

  function handleClick(): void {
    setFormCompleteEvent();
    onSubmit();
  }

  // Localized content constants
  const sectionHeading = getTextContent('consent.contactReason.heading');
  const sectionToggleTitle = getTextContent(`${formData?.lodgement}.details.sectionHeading`);
  const claimNumberTitle = getTextContent('consent.contactReason.claimNumber');
  const claimNumberInfoText = getTextContent('consent.contactReason.claimNumber.infoText');
  const claimNumberLabel = getTextContent('consent.contactReason.claimNumber.label');
  const policyNumberTitle = getTextContent('consent.contactReason.policyNumber');
  const policyNumberInfoText = getTextContent('consent.contactReason.policyNumber.infoText');
  const policyNumberLabel = getTextContent('consent.contactReason.policyNumber.label');
  const contactInfoComplaintText = getTextContent('consent.contactReason.contactInformation');
  const contactInfoFeedbackText = getTextContent('Feedback.Compliment.contactReason.contactInformation');
  const indOrBusHeading = getTextContent('consent.individualOrBusiness.sectionHeading');
  const indText0 = getTextContent('consent.individualOrBusiness.0.text');
  const indText1 = getTextContent('consent.individualOrBusiness.1.text');
  const businessNameTitle = getTextContent('insured.otherDetails.businessName');
  const businessNameHelpText = getTextContent('insured.otherDetails.businessName.helpText');
  const businessNameLabel = getTextContent('insured.otherDetails.businessName.label');
  const isSmallBusinessTitle = getTextContent('insured.otherDetails.isSmallBusiness');
  const isSmallBusinessText0 = getTextContent('insured.otherDetails.isSmallBusiness.0.text');
  const isSmallBusinessText1 = getTextContent('insured.otherDetails.isSmallBusiness.1.text');

  return (
    <FormCard variant="sectionTitle" title={sectionHeading}>
      {isComplaint ? (
        <Controller
          control={control}
          name="policyOrClaim"
          rules={{ required: '' }}
          render={({ field }) => (
            <Toggle
              {...field}
              required
              title={sectionToggleTitle}
              onChange={(event) => {
                const value = event.target.value as string;
                field.onChange(value);
                triggerValidation('policyOrClaim');
                setFormStartEvent();
                setValue('policyOrClaim', value);
              }}
              optionList={options.map((option) => ({
                autoComplete: 'off',
                id: option.value,
                value: option.value,
                label: option.text ?? option.value,
                defaultChecked: policyOrClaim === option.value,
                'data-test': 'policyOrClaim',
                'data-track': 'policyOrClaim',
                'data-track-value': 'capture',
              }))}
            />
          )}
          defaultValue={policyOrClaim}
        />
      ) : (
        <>
          <Controller
            name="policyOrClaim"
            control={control}
            defaultValue={policyOrClaim}
            rules={{ required: '' }}
            render={({ field }) => (
              <RadioGroup
                {...field}
                title={sectionToggleTitle}
                required
                styleVariant="skinny"
                direction="vertical"
                radioList={options.map(({ text, value }) => ({
                  label: text,
                  value,
                  id: value,
                }))}
                data-track="policyOrClaim"
                data-track-value="capture"
                onChange={(value) => {
                  field.onChange(value);
                  setFormStartEvent();
                }}
              />
            )}
          />
          <FormSpacer type="input" />
        </>
      )}

      {policyOrClaim === 'claim' && (
        <>
          <FormGroupLabel
            alignCenter
            required
            titleSuffix={<InformationPopUp message={claimNumberInfoText} />}
            title={claimNumberTitle}
            subtitle={isComplaint ? contactInfoComplaintText : contactInfoFeedbackText}
          />
          <FormSpacer type="input" />
          <Controller
            name="claimNumber"
            control={control}
            defaultValue=""
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={claimNumberLabel}
                id="claimNumber"
                value={field.value as TextFieldValue}
                error={!!errors.claimNumber}
                helperText={errors?.claimNumber?.message || null}
                inputProps={{
                  maxLength: 50,
                  minLength: 4,
                }}
                data-track="claimNumber"
                data-track-value="capture"
                data-test="claimNumber"
              />
            )}
          />
        </>
      )}

      {policyOrClaim === 'policy' && (
        <>
          <FormGroupLabel
            alignCenter
            required
            title={policyNumberTitle}
            titleSuffix={<InformationPopUp message={policyNumberInfoText} />}
            subtitle={isComplaint ? contactInfoComplaintText : contactInfoFeedbackText}
          />
          <FormSpacer type="input" />

          <Controller
            name="policyNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.policyNumber}
                helperText={errors?.policyNumber?.message || null}
                label={policyNumberLabel}
                id="policyNumber"
                value={field.value as TextFieldValue}
                data-track="policyNumber"
                data-track-value="capture"
                inputProps={{
                  maxLength: 50,
                  minLength: 4,
                }}
                data-test="policyNumber"
              />
            )}
          />
        </>
      )}

      <Controller
        name="indOrBus"
        control={control}
        rules={{ required: '' }}
        render={({ field }) => (
          <Toggle
            {...field}
            title={indOrBusHeading}
            required
            optionList={[
              {
                text: indText0,
                value: 'individual',
              },
              {
                text: indText1,
                value: 'business',
              },
            ].map((option) => ({
              autoComplete: 'off',
              id: option.value,
              value: option.value,
              label: option.text ?? option.value,
              defaultChecked: indOrBus === option.value,
              'data-test': 'indOrBus',
              'data-track': 'indOrBus',
              'data-track-value': 'capture',
            }))}
            onChange={(event) => {
              const value = event.target.value as IndOrBus;
              field.onChange(value);
              setValue('indOrBus', value);
              triggerValidation('indOrBus');
            }}
          />
        )}
      />

      {indOrBus === 'business' && (
        <>
          <FormGroupLabel
            required
            alignCenter
            title={businessNameTitle}
            titleSuffix={<InformationPopUp message={businessNameHelpText} />}
          />
          <FormSpacer type="input" />
          <Controller
            name="businessName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="businessName"
                error={!!errors.businessName}
                value={field.value as TextFieldValue}
                label={businessNameLabel}
                data-track="businessName"
                data-track-value="capture"
                inputProps={{
                  maxLength: 100,
                  minLength: 1,
                }}
                data-test="businessName"
                helperText={errors.businessName ? errors.businessName?.message : null}
              />
            )}
          />
          {isComplaint && (
            <>
              <Controller
                name="isSmallBusiness"
                control={control}
                defaultValue={isSmallBusiness}
                rules={{ required: '' }}
                render={({ field }) => (
                  <Toggle
                    {...field}
                    required
                    title={isSmallBusinessTitle}
                    onChange={(event) => {
                      const value = event.target.value as string;
                      field.onChange(value);
                      setValue('isSmallBusiness', value);
                      triggerValidation('isSmallBusiness');
                    }}
                    optionList={[
                      {
                        text: isSmallBusinessText0,
                        value: 'yes',
                      },
                      {
                        text: isSmallBusinessText1,
                        value: 'no',
                      },
                    ].map((option) => ({
                      autoComplete: 'off',
                      id: option.value,
                      value: option.value,
                      label: option.text ?? option.value,
                      defaultChecked: isSmallBusiness === option.value,
                      'data-test': 'isSmallBusiness',
                      'data-track': 'isSmallBusiness',
                      'data-track-value': 'capture',
                    }))}
                  />
                )}
              />
            </>
          )}
        </>
      )}

      {showNextButton && (
        <>
          {!isComplaint && <FormSpacer type="formgroup" />}
          {isComplaint && <FormSpacer type="input" />}
          <Button type="button" label="Next" disabled={!enableNextButton} onClick={handleClick} />
        </>
      )}
    </FormCard>
  );
};

export default ContactReason;
